import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { WithholdingRate } from '../../../types/bookkeeping.js';

/** percentage bigint (×1,000,000) as a decimal string, e.g. "15000000" → "15.0%" */
function formatPercentage(raw: string): string {
  const n = BigInt(raw);
  const whole = n / 1_000_000n;
  const frac = n % 1_000_000n;
  const fracStr = frac.toString().padStart(6, '0').replace(/0+$/, '') || '0';
  return `${whole}.${fracStr}%`;
}

const WITHHOLDING_KINDS = ['income', 'iva'] as const;

const WithholdingRateSchema = z.object({
  code: z.string().min(1).max(20),
  name: z.string().min(1).max(100),
  kind: z.enum(WITHHOLDING_KINDS),
  percentage_str: z
    .string()
    .min(1)
    .regex(/^\d+(\.\d+)?$/, 'Must be a non-negative number'),
  account_id_receivable: z.string().min(1),
  account_id_payable: z.string().optional(),
});
type WithholdingRateForm = z.infer<typeof WithholdingRateSchema>;

function WithholdingRateModal({
  rate,
  onClose,
  onSaved,
}: {
  rate?: WithholdingRate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('bookkeeping');
  const isEdit = !!rate;

  const toPercentageStr = (raw?: string): string => {
    if (!raw) return '';
    const n = BigInt(raw as string);
    const whole = n / 1_000_000n;
    const frac = n % 1_000_000n;
    const fracStr = frac.toString().padStart(6, '0').replace(/0+$/, '') || '00';
    return `${whole}.${fracStr}`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<WithholdingRateForm>({
    resolver: zodResolver(WithholdingRateSchema),
    defaultValues: {
      code: rate?.code ?? '',
      name: rate?.name ?? '',
      kind: rate?.kind ?? 'income',
      percentage_str: toPercentageStr(rate?.percentage),
      account_id_receivable: rate?.account_id_receivable ?? '',
      account_id_payable: rate?.account_id_payable ?? '',
    },
  });

  const toPercentageBigInt = (str: string): string => {
    const parts = str.split('.');
    const whole = parts[0] ?? '0';
    const frac = parts[1] ?? '';
    const fracPadded = frac.padEnd(6, '0').slice(0, 6);
    return (BigInt(whole) * 1_000_000n + BigInt(fracPadded)).toString();
  };

  const createMutation = useMutation({
    mutationFn: (data: WithholdingRateForm) =>
      api('/bookkeeping/sales/withholding-rates', {
        method: 'POST',
        body: JSON.stringify({
          code: data.code,
          name: data.name,
          kind: data.kind,
          percentage: toPercentageBigInt(data.percentage_str),
          account_id_receivable: data.account_id_receivable,
          ...(data.account_id_payable ? { account_id_payable: data.account_id_payable } : {}),
        }),
      }),
    onSuccess: onSaved,
  });

  const updateMutation = useMutation({
    mutationFn: (data: WithholdingRateForm) =>
      api(`/bookkeeping/sales/withholding-rates/${rate?.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          code: data.code,
          name: data.name,
          kind: data.kind,
          percentage: toPercentageBigInt(data.percentage_str),
          account_id_receivable: data.account_id_receivable,
          ...(data.account_id_payable ? { account_id_payable: data.account_id_payable } : {}),
        }),
      }),
    onSuccess: onSaved,
  });

  const onSubmit = handleSubmit((data) =>
    isEdit ? updateMutation.mutate(data) : createMutation.mutate(data),
  );

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit
            ? t('sales.withholding_rates.editTitle', { defaultValue: 'Editar tasa de retención' })
            : t('sales.withholding_rates.new')}
        </h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('sales.tax_rates.code')} *</span>
              <input
                {...register('code')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
              {errors.code && <span className="text-xs text-red-600">{errors.code.message}</span>}
            </label>
            <label className="block">
              <span className="text-sm">{t('sales.tax_rates.kind')} *</span>
              <select
                {...register('kind')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              >
                {WITHHOLDING_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`sales.withholding_rates.kinds.${k}`, { defaultValue: k })}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-sm">{t('sales.tax_rates.name')} *</span>
            <input {...register('name')} className="mt-1 w-full rounded border px-3 py-2 text-sm" />
            {errors.name && <span className="text-xs text-red-600">{errors.name.message}</span>}
          </label>
          <label className="block">
            <span className="text-sm">{t('sales.tax_rates.percentage')} * (%)</span>
            <input
              {...register('percentage_str')}
              placeholder="e.g. 30 or 10.00"
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
            {errors.percentage_str && (
              <span className="text-xs text-red-600">{errors.percentage_str.message}</span>
            )}
          </label>
          <label className="block">
            <span className="text-sm">
              {t('sales.withholding_rates.account_receivable', {
                defaultValue: 'Cuenta retenciones por cobrar',
              })}{' '}
              *
            </span>
            <input
              {...register('account_id_receivable')}
              placeholder="account UUID"
              className="mt-1 w-full rounded border px-3 py-2 text-sm font-mono text-xs"
            />
            {errors.account_id_receivable && (
              <span className="text-xs text-red-600">{errors.account_id_receivable.message}</span>
            )}
          </label>
          <label className="block">
            <span className="text-sm">
              {t('sales.withholding_rates.account_payable', {
                defaultValue: 'Cuenta retenciones por pagar',
              })}
            </span>
            <input
              {...register('account_id_payable')}
              placeholder="account UUID (opcional)"
              className="mt-1 w-full rounded border px-3 py-2 text-sm font-mono text-xs"
            />
            {errors.account_id_payable && (
              <span className="text-xs text-red-600">{errors.account_id_payable.message}</span>
            )}
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {isEdit ? 'Guardar' : t('sales.withholding_rates.new')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function WithholdingRates() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WithholdingRate | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bk-withholding-rates', activeOrgId],
    queryFn: () =>
      api<{ withholding_rates: WithholdingRate[] }>('/bookkeeping/sales/withholding-rates'),
    enabled: !!activeOrgId,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/sales/withholding-rates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bk-withholding-rates'] }),
  });

  const handleSaved = () => {
    void qc.invalidateQueries({ queryKey: ['bk-withholding-rates'] });
    setAddOpen(false);
    setEditTarget(null);
  };

  const rates = data?.withholding_rates ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('sales.withholding_rates.title')}</h1>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('sales.withholding_rates.new')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('sales.tax_rates.code')}</th>
                <th className="px-3 py-2">{t('sales.tax_rates.name')}</th>
                <th className="px-3 py-2">{t('sales.tax_rates.kind')}</th>
                <th className="px-3 py-2 text-right">{t('sales.tax_rates.percentage')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                    {t('sales.withholding_rates.empty', {
                      defaultValue: 'No hay tasas de retención aún.',
                    })}
                  </td>
                </tr>
              )}
              {rates.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {t(`sales.withholding_rates.kinds.${r.kind}`, { defaultValue: r.kind })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatPercentage(r.percentage)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.is_seeded ? (
                      <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600">
                        {t('sales.tax_rates.seeded_badge')}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditTarget(r)}
                          className="mr-2 text-xs text-blue-600 hover:underline"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => deactivate.mutate(r.id)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Desactivar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && <WithholdingRateModal onClose={() => setAddOpen(false)} onSaved={handleSaved} />}
      {editTarget && (
        <WithholdingRateModal
          rate={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
